/***
*
*   PASSWORD
*   Update the users password
*
*   if the user has a password show old and new inputs
*   if no existing password (eg. in case of social signin)
*   then allow the user to set a password on their account
*
**********/
import Styles from './account.module.scss';
import React, { Fragment, useContext, useCallback, useState, useEffect } from 'react';
import { AuthContext, AccountNav, Animate, Message, Card, ViewContext } from 'components/lib';
import {
    Box,
    TextField,
    IconButton,
    Dialog,
    Typography,
    Autocomplete,
    Grid,
    Divider,
    InputAdornment,
    LinearProgress
} from '@mui/material';
import codes from 'country-calling-code';
import axios from 'axios';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { AivagamButton } from 'components/global/AivagamButton/AivagamButton';
import { OtpVerification } from 'views/auth/signin/otpVerify';
import { Close } from '@mui/icons-material';
import FeatherIcon from 'feather-icons-react';
import CaptchaField from 'components/global/CaptchaField/CaptchaField';

const textFieldProps = {
    fullWidth: true,
    variant: "outlined",
    slotProps: { inputProps: { readOnly: true } },
    size: "medium",
    sx: {
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: 'inherit',
            }
        }
    }
};

export function Password(props) {
    const { t } = props;
    const context = useContext(AuthContext);
    const viewContext = useContext(ViewContext);
    const [done, setDone] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editField, setEditField] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const [countryCode, setCountryCode] = useState('91')

    const [formData, setFormData] = useState({
        oldPassword: '',
        newPassword: '',
    });
    const [touched, setTouched] = useState({});
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isOTPSent, setIsOTPSent] = useState(false);
    const [OTP, setOTP] = useState('');
    const [error, setError] = useState('');
    const [captcha, setCaptcha] = useState({
        image: "",
        id: "",
        input: ""
    })

    //useEffects
    useEffect(() => {
        if (dialogOpen && !isOTPSent) {
            refreshCaptcha();
        }
    }, [dialogOpen, isOTPSent])

    const refreshCaptcha = async () => {
        try {
            const res = await axios.get("/api/auth/captcha");
            setCaptcha({
                image: res.data.image,
                id: res.data.captchaId,
                input: "",
            });
        } catch (err) {
            context.notification.show("Failed to load captcha. Please try again later", "error", true);
        }
    }

    const getPasswordStrength = (password) => {
        if (!password) return 0;

        let strength = 0;

        if (password.length > 0) strength += 10
        // Length check
        if (password.length >= 8) strength += 15;

        // Contains uppercase
        if (/[A-Z]/.test(password) && password.length >= 8) strength += 25;

        // Contains number
        if (/\d/.test(password) && password.length >= 8) strength += 25;

        // Contains special character
        if (/[^A-Za-z0-9]/.test(password) && password.length >= 8) strength += 25;

        return strength;
    };

    const getStrengthLabel = (strength) => {
        if (strength === 0) return props.t('account.password.strength.noPassword') || 'No Password';
        if (strength <= 25) return props.t('account.password.strength.tooWeak') || 'Too Weak';
        if (strength <= 50) return props.t('account.password.strength.weak') || 'Weak';
        if (strength <= 75) return props.t('account.password.strength.fair') || 'Fair';
        return props.t('account.password.strength.strong') || 'Strong';
    };

    const getStrengthColor = (strength) => {
        if (strength <= 25) return '#f44336'; // red
        if (strength <= 50) return '#ff9800'; // orange
        if (strength <= 75) return '#2196f3'; // blue
        return '#4caf50'; // green
    };

    const passwordStrength = getPasswordStrength(formData.newPassword);
    const strengthLabel = getStrengthLabel(passwordStrength);
    const strengthColor = getStrengthColor(passwordStrength);

    const validate = useCallback((data) => {
        const newErrors = {};
        const { oldPassword, newPassword } = data;
        if (context.user.has_password && !oldPassword) {
            newErrors.oldPassword = t('account.password.error.oldPasswordRequired') || 'Please enter your current password';
        }

        if (!newPassword) {
            newErrors.newPassword = t('account.password.error.newPasswordRequired') || 'New password is required';
        } else {
            if (newPassword.length < 8) {
                newErrors.newPassword = t('account.password.error.minLength') || 'Password must be at least 8 characters long';
            }
            if (!/[A-Z]/.test(newPassword)) {
                newErrors.newPassword = t('account.password.error.uppercase') || 'Must include an uppercase letter';
            }
            if (!/[a-z]/.test(newPassword)) {
                newErrors.newPassword = t('account.password.error.lowercase') || 'Must include a lowercase letter';
            }
            if (!/\d/.test(newPassword)) {
                newErrors.newPassword = t('account.password.error.number') || 'Must include a number';
            }
            if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(newPassword)) {
                newErrors.newPassword = t('account.password.error.specialCharacter') || 'Must include a special character';
            }
            if (context.user.has_password && oldPassword === newPassword) {
                newErrors.newPassword = t('account.password.error.sameAsOld') || 'New password cannot be the same as old password';
            }
        }
        return newErrors;
    }, [context.user.has_password, t]);

    const handleBlur = (field) => {
        setTouched((prev) => ({ ...prev, [field]: true }));
        const updatedErrors = validate(formData);
        setErrors(updatedErrors);
    };

    const handleChange = (field) => (e) => {
        const value = e.target.value;
        const updatedFormData = { ...formData, [field]: value };
        setFormData(updatedFormData);
        const updatedErrors = validate(updatedFormData);
        setErrors(updatedErrors);
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        setTouched({ oldPassword: true, newPassword: true });
        const validationErrors = validate(formData);
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) return;
        setLoading(true);

        try {
            const response = await axios.put('/api/user/password', {
                oldpassword: formData.oldPassword,
                newpassword: formData.newPassword,
                has_password: context.user.has_password
            });
            if (response.status === 200) {
                setDone(true);
                context.update({ has_password: true })
                viewContext.notification.show(response?.data?.message || 'Password Updated', 'success', true);
            }
        } catch (error) {
            if (error.response?.data?.message) {
                viewContext.notification.show(error.response.data?.message, 'error');
            } else {
                viewContext.notification.show(error.response.data?.error, 'error');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (field) => {
        setEditField(field);
        setInputValue(field === 'email' ? context.user.email : context.user.phone_number);
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setEditField(null);
        setInputValue('');
        setIsOTPSent(false);
        setError('');
    };

    const isEmailType = editField === 'email';

    const validateInput = useCallback((value) => {
        if (isEmailType) {
            const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return re.test(value) ? '' : t('account.password.email.edit') || 'Enter a valid email';
        } else {
            const re = /^\d{6,14}$/;
            return re.test(value) ? '' : t('account.password.phone.edit') || 'Enter a valid phone number';
        }
    }, [isEmailType, t]);

    const handleInputChange = useCallback((e) => {
        const { value } = e.target;
        setInputValue(value);
        setError(validateInput(value));
    }, [validateInput]);

    const handleCaptchaInput = (input) => {
        setCaptcha((prev) => ({ ...prev, input: input }));
    }

    const handleSendOTP = async () => {
        const validationError = validateInput(inputValue);
        if (validationError) {
            setError(validationError);
            return;
        }
        setLoading(true);
        const queryParams = new URLSearchParams({
            [isEmailType ? 'email' : 'mobile']: true,
            set_up: true,
        }).toString();

        let options = {
            method: 'POST',
            url: `/api/auth/otp?${queryParams}`,
            data: isEmailType
                ? { email: inputValue, first_name: context.user?.first_name || 'User', captcha_id: captcha.id, captcha_input: captcha.input }
                : { mobile_number: `+${countryCode} ${inputValue}`, captcha_id: captcha.id, captcha_input: captcha.input }
        }

        axios
            .request(options)
            .then((response) => {
                if ((response.data.otp_sent.email_otp === true) || (response.data.otp_sent.mobile_otp === true)) {
                    viewContext.notification.show(response.data?.message || props.t('account.password.form.otp.sent'), 'success', true);
                    setIsOTPSent(true);
                    setOTP('');
                } else {
                    viewContext.notification.show(response.data.message, 'error', true);
                }
            })
            .catch((error) => {
                const data = error.response?.data
                const message = data?.message || data;
                if (['CAPTCHA_INVALID', 'CAPTCHA_EXPIRED'].includes(data?.errorCode) && data?.captcha) {
                    setCaptcha({ id: data.captcha.captchaId, image: data.captcha.image, input: '' })
                }
                viewContext.notification.show(message, 'error', true);
            })
            .finally(() => {
                setLoading(false)
            })
    };

    const handleResendOTP = async () => {
        setLoading(true);
        const queryParams = new URLSearchParams({
            [isEmailType ? 'email' : 'mobile']: true
        }).toString();

        let options = {
            method: 'POST',
            url: `/api/auth/otp/resend?${queryParams}`,
            data: isEmailType
                ? { email: inputValue, first_name: context.user?.first_name || 'User' }
                : { mobile_number: `+${countryCode} ${inputValue}` }
        }

        axios
            .request(options)
            .then((response) => {
                if ((response.data.otp_sent.email_otp === true) || (response.data.otp_sent.mobile_otp === true)) {
                    viewContext.notification.show(response.data.message, 'success', true);
                } else {
                    viewContext.notification.show(response.data.message, 'error');
                }
            })
            .catch((error) => {
                const data = error.response?.data
                const message = data?.message || data;
                if (['CAPTCHA_INVALID', 'CAPTCHA_EXPIRED'].includes(data?.errorCode) && data?.captcha) {
                    setCaptcha({ id: data.captcha.captchaId, image: data.captcha.image, input: '' })
                }
                viewContext.notification.show(message, 'error', true);
                if (isOTPSent) {
                    setIsOTPSent(false);
                    setOTP('');
                }
            })
            .finally(() => {
                setLoading(false);
            })
    };

    const handleVerifyOTP = async () => {
        setLoading(true);

        const options = {
            method: 'POST',
            url: '/api/auth/otp/verify',
            data: {
                ...(isEmailType
                    ? {
                        email_otp: OTP,
                        email_verification_required: true,
                        email: inputValue
                    }
                    : {
                        mobile_otp: OTP,
                        mobile_verification_required: true,
                        mobile_number: inputValue,
                        country_code: countryCode
                    }),
            },
        };
        axios
            .request(options)
            .then(function(response) {
                context.update(isEmailType ? { email: inputValue } : { phone_number: inputValue, country_code: countryCode })
                viewContext.notification.show(props.t('account.password.form.otp.verified') || "Verification successful", 'success', true);
                handleDialogClose()
            })
            .catch((error) => {
                if (error.response?.data?.message) {
                    viewContext.notification.show(error.response.data?.message, 'error');
                } else {
                    viewContext.notification.show(error.response.data, 'error');
                }
            })
            .finally(() => {
                setLoading(false);
                setIsOTPSent(false);
                setOTP('');
            });
    };

    return (
        <Fragment>
            <AccountNav />
            <Animate>
                <Card className={Styles.cardContainer} restrictWidth>
                    <Typography variant="h6" gutterBottom>
                        {props.t('account.password.title') || 'Account Details'}
                    </Typography>
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', margin: '1em 0' }}>
                            <TextField
                                {...textFieldProps}
                                value={context.user.email}
                                label={props.t('account.password.email.title')}
                                disabled
                                required
                            />
                            <IconButton
                                sx={{
                                    ml: 1,
                                    p: 1,
                                    color: '#1450a0',
                                    '&:hover': {
                                        color: '#165bb6',
                                    },
                                }}
                                size='small'
                                onClick={() => handleEdit('email')}
                                aria-label="Edit email"
                            >
                                <FeatherIcon icon='edit' />
                            </IconButton>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '1em' }}>
                            <Grid container spacing={2}>
                                <Grid item xs={4}>
                                    <Autocomplete
                                        options={codes}
                                        getOptionLabel={(option) => `${option.isoCode3} +${option.countryCodes[0]}`}
                                        value={codes.find(option => option.countryCodes[0] === countryCode) || null}
                                        onChange={(event, newValue) => {
                                            setCountryCode(newValue.countryCodes[0]);
                                        }}
                                        disableClearable
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Country"
                                                name="countryCode"
                                                required
                                                disabled
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={8}>
                                    <TextField
                                        {...textFieldProps}
                                        value={context.user.phone_number}
                                        label={props.t('account.password.phone.label')}
                                        disabled
                                        required
                                    />
                                </Grid>
                            </Grid>
                            <IconButton
                                sx={{
                                    ml: 1,
                                    p: 1,
                                    color: '#1450a0',
                                    '&:hover': {
                                        color: '#165bb6',
                                    },
                                }}
                                size='small'
                                onClick={() => handleEdit('phone')}
                                aria-label="Edit phone number"
                            >
                                <FeatherIcon icon='edit' />
                            </IconButton>
                        </Box>
                    </Grid>
                    <Divider style={{ margin: '20px 0' }} orientation="horizontal" />
                    <Typography variant="h6" gutterBottom>
                        {props.t('account.password.label')}
                    </Typography>
                    {!done ?
                        <Box component="form" onSubmit={handleSubmit}>
                            <div id='oldPasswordInputContainer' style={{ display: 'flex', flexDirection: 'column', marginBottom: '0.5em', marginTop: '0.5em' }}>
                                <TextField
                                    label={props.t('account.password.form.old_password.label')}
                                    type={showPassword ? 'text' : 'password'}
                                    value={formData.oldPassword}
                                    onChange={handleChange('oldPassword')}
                                    onBlur={() => handleBlur('oldPassword')}
                                    disabled={loading}
                                    error={touched.oldPassword && !!errors.oldPassword}
                                    helperText={touched.oldPassword && errors.oldPassword}
                                    required
                                    slotProps={{
                                        input: {
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="Toggle old password visibility"
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        edge="end"
                                                    >
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }
                                    }}
                                />
                            </div>

                            <div id='newPasswordInputContainer' style={{ display: 'flex', flexDirection: 'column', marginBottom: '0.5em', marginTop: '0.5em' }}>
                                <TextField
                                    label={props.t('account.password.form.new_password.label')}
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={formData.newPassword || ''}
                                    onChange={handleChange('newPassword')}
                                    onBlur={() => handleBlur('newPassword')}
                                    error={!!errors.newPassword}
                                    helperText={errors.newPassword}
                                    disabled={loading}
                                    required
                                    slotProps={{
                                        input: {
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle confirm password visibility"
                                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                        edge="end"
                                                    >
                                                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }
                                    }}
                                />
                            </div>
                            {formData.newPassword && (
                                <div id='passwordStrengthContainer' className={Styles.passwordStrengthContainer}>
                                    <div id='passwordStrength' className={Styles.passwordStrength}>
                                        <Typography variant="caption">Password Strength: {strengthLabel}</Typography>
                                        <Typography variant="caption" style={{ color: strengthColor }}>{passwordStrength}%</Typography>
                                    </div>
                                    <LinearProgress
                                        variant="determinate"
                                        value={passwordStrength}
                                        sx={{
                                            height: 6,
                                            borderRadius: 3,
                                            backgroundColor: 'rgba(0,0,0,0.08)',
                                            '& .MuiLinearProgress-bar': {
                                                backgroundColor: strengthColor,
                                            }
                                        }}
                                    />
                                </div>
                            )}
                            <div className='flex mt-4'>
                                <AivagamButton
                                    buttonText={props.t('account.password.form.button')}
                                    type="confirm"
                                    disabled={loading ||
                                        Object.keys(errors).length > 0 ||
                                        (context.user.has_password && !formData.oldPassword) ||
                                        !formData.newPassword.length ||
                                        passwordStrength !== 100}
                                    classStyles={{
                                        alignItems: 'center',
                                        fontWeight: 600,
                                        fontSize: '100%',
                                        width: '100%'
                                    }}
                                />
                            </div>
                        </Box>
                        :
                        <Message
                            type='success'
                            title={props.t('account.password.success_message.title')}
                            text={props.t('account.password.success_message.text')}
                        />
                    }
                </Card>
            </Animate>

            <Dialog
                open={dialogOpen}
                onClose={() => {/* Prevents Dialog from being closed on clickaway */ }}
                fullWidth
                PaperProps={{
                    sx: {
                        width: '1000px',
                        maxWidth: '60%',
                        height: '600px',
                        maxHeight: '65vh',
                    }
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%', padding: '0.25em 0.5em' }}>
                        <IconButton onClick={handleDialogClose}>
                            <Close fontSize='small' />
                        </IconButton>
                    </div>
                    {isOTPSent ? (
                        <OtpVerification
                            OTP={OTP}
                            setOTP={setOTP}
                            onVerify={handleVerifyOTP}
                            onResend={handleResendOTP}
                            onSubmitNewOtp={handleSendOTP}
                            onEdit={() => { }}
                            signUpMethod={editField}
                            editable={false}
                            destination={inputValue}
                            loading={loading}
                            t={props.t}
                        />
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', width: '100%', padding: '1.5em' }}>
                            <Typography variant='h5' sx={{ color: '#1450a0', fontWeight: '600', marginBottom: '1em' }}>
                                Enter the New {editField === 'email' ? 'Email Address' : 'Phone Number'}
                            </Typography>
                            <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-start', margin: '1em 0', padding: '0 1em' }}>
                                <Typography variant='body1'>
                                    Enter the new {editField === 'email' ? 'email address' : 'phone number'} and we will send an OTP to verify the contact.
                                </Typography>
                            </div>
                            <div style={{ display: 'flex', width: '100%', flexDirection: 'column', padding: '0.75em 1.5em', gap: '1em' }}>
                                {editField === 'email' ? (
                                    <TextField
                                        autoFocus
                                        fullWidth
                                        variant="outlined"
                                        label={props.t('account.password.email.title')}
                                        type={'email'}
                                        value={inputValue}
                                        onChange={handleInputChange}
                                        error={!!error}
                                        helperText={error}
                                    />
                                ) : (
                                    <Grid container spacing={2}>
                                        <Grid item xs={4}>
                                            <Autocomplete
                                                options={codes}
                                                getOptionLabel={(option) => `${option.isoCode3} +${option.countryCodes[0]}`}
                                                value={codes.find(option => option.countryCodes[0] === countryCode) || null}
                                                onChange={(event, newValue) => {
                                                    setCountryCode(newValue.countryCodes[0]);
                                                }}
                                                disableClearable
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label={props.t('account.password.form.country.label') || "Country"}
                                                        name="countryCode"
                                                        required
                                                    />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={8}>
                                            <TextField
                                                {...textFieldProps}
                                                value={inputValue}
                                                label={props.t('account.password.phone.label')}
                                                required
                                                onChange={handleInputChange}
                                                error={!!error}
                                                helperText={error}
                                            />
                                        </Grid>
                                    </Grid>
                                )}
                                {!!captcha?.image && (
                                    <CaptchaField
                                        captcha={captcha}
                                        onCaptchaChange={handleCaptchaInput}
                                        onRefreshCaptcha={refreshCaptcha}
                                        error={!/^-?\d+$/.test(captcha.input)}
                                        helperText={
                                            !/^-?\d+$/.test(captcha.input) ? "Enter the result" : ""
                                        }
                                    />
                                )}
                            </div>

                            <div style={{ marginTop: '1em' }}>
                                <AivagamButton
                                    buttonText={props.t('account.password.form.verify')}
                                    type="confirm"
                                    disabled={loading || (editField === 'email' ?
                                        inputValue?.toLowerCase() === context?.user?.email?.toLowerCase()
                                        : inputValue?.toLowerCase() === context.user.phone_number?.toLowerCase()
                                    )}
                                    classStyles={{
                                        alignItems: 'center',
                                        fontWeight: 600,
                                        width: '10em',
                                        padding: '0.75em 2em',
                                        boxShadow: '0 4px 6px rgba(0,0,0,0.3)'
                                    }}
                                    onClick={handleSendOTP}
                                />
                            </div>
                        </div>
                    )}

                </div>
            </Dialog>
        </Fragment>
    );
}
